import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import { getUserData } from '@/auth/utils'

import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function useUsersList() {
  // Use toast
  const toast = useToast()

  const refUserListTable = ref(null)
  const padrinhoCodigo = ref(null)

  // Table Handlers
  const tableColumns = [
    { key: 'id', sortable: true, label: '#' },
    { key: 'client', sortable: true, label: 'Cliente' },
    { key: 'login', sortable: true, label: 'Login' },
    { key: 'id_grupousuario', sortable: true, label: 'Grupo' },
    { key: 'ativo', sortable: true, label: 'Status' },
    { key: 'data_inclusao', sortable: true, label: 'Data Criação' },
    { key: 'data_alteracao', sortable: true, label: 'Data Atualização' },
  ]
  const perPage = ref(10)
  const totalUsers = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const roleFilter = ref('')
  const grupoFilter = ref('')
  const statusFilter = ref('')

  const dataMeta = computed(() => {
    const localItemsCount = refUserListTable.value ? refUserListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalUsers.value,
    }
  })

  const refetchData = () => {
    refUserListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, roleFilter, grupoFilter, statusFilter], () => {
    refetchData()
  })

  const fetchGrupos = (ctx, callback) => {
    store
      .dispatch('app-adm-usuario/fetchGrups', {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        role: roleFilter.value,
        id_grupousuario: grupoFilter.value,
        ativo: statusFilter.value,
      })
      .then(response => {
        const { users, total } = response.data

        callback(users)
        totalUsers.value = total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching users list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const getStatus = status => {
    if (['1', 1, 'active', 'ativo', true, 'true'].indexOf(status) !== -1) {
      return 1
    }
    if (['0', 0, 'inactive', 'inativo', false, 'false'].indexOf(status) !== -1) {
      return 0
    }
    return null
  }

  const fetchPadrinhoByUserId = async userId => {
    try {
      const response = await store.dispatch('app-adm-usuario/fetchPadrinhoByUserId', userId)
      padrinhoCodigo.value = response.codigo
      return response
    } catch (error) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Error fetching padrinho',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
      throw error
    }
  }

  const fetchUsers = (ctx, callback) => {
    const userData = getUserData()
    console.log('Fetching users with filters:', userData)
    store
      .dispatch('app-adm-usuario/fetchUsers', {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        role: roleFilter.value,
        id_grupousuario: grupoFilter.value,
        ativo: getStatus(statusFilter.value),
        codigo: userData.padrinho.codigo,
      })
      .then(response => {
        const { users, total } = response.data

        callback(users)
        totalUsers.value = total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching users list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = role => {
    if (role === 'Admin B2B') return 'primary'
    if (role === 'Cliente B2B') return 'warning'
    if (role === 'Admin') return 'danger'
    return 'primary'
  }

  const resolveUserRoleIcon = role => {
    if (role === 'Cliente B2B') return 'UserIcon'
    if (role === 'Admin B2B') return 'SettingsIcon'
    if (role === 'Admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolveUserStatusVariant = status => {
    if (status === 'pending') return 'warning'
    if (status === 1) return 'success'
    if (status === 0) return 'secondary'
    return 'primary'
  }

  return {
    fetchGrupos,
    fetchUsers,
    fetchPadrinhoByUserId,
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    refetchData,

    // Extra Filters
    roleFilter,
    grupoFilter,
    statusFilter,
  }
}